"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NotionBlocksMarkdownParser = void 0;
const external_video_util_1 = require("./external-video.util");
const EOL_MD = '\n';
class NotionBlocksMarkdownParser {
    constructor(options) {
        this.parserOptions = Object.assign({ imageAsFigure: true, emptyParagraphToNonBreakingSpace: false }, (options || {}));
    }
    static getInstance(options) {
        if (!this.instance) {
            this.instance = new this(options);
        }
        return this.instance;
    }
    parse(blocks, depth = 0) {
        return blocks
            .reduce((markdown, childBlock) => {
            let childBlockString = '';
            if (childBlock.has_children && childBlock[childBlock.type].children) {
                childBlockString = ' '
                    .repeat(depth)
                    .concat(childBlockString, this.parse(childBlock[childBlock.type].children, depth + 2));
            }
            if (childBlock.type === 'unsupported') {
                markdown += 'NotionAPI Unsupported'.concat(EOL_MD.repeat(2), childBlockString);
            }
            if (childBlock.type === 'paragraph') {
                markdown += this.parseParagraph(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'code') {
                markdown += this.parseCodeBlock(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'quote') {
                markdown += this.parseQuoteBlock(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'callout') {
                markdown +=
                    this.parseCalloutBlock(childBlock).concat(childBlockString);
            }
            if (childBlock.type.startsWith('heading_')) {
                const headingLevel = Number(childBlock.type.split('_')[1]);
                markdown += this.parseHeading(childBlock, headingLevel).concat(childBlockString);
            }
            if (childBlock.type === 'bulleted_list_item') {
                markdown +=
                    this.parseBulletedListItems(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'numbered_list_item') {
                markdown +=
                    this.parseNumberedListItems(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'to_do') {
                markdown += this.parseTodoBlock(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'toggle') {
                markdown += this.parseToggleBlock(childBlock).replace('{{childBlock}}', childBlockString);
            }
            if (childBlock.type === 'image') {
                markdown += this.parseImageBlock(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'embed') {
                markdown += this.parseEmbedBlock(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'audio') {
                markdown += this.parseAudioBlock(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'video') {
                markdown += this.parseVideoBlock(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'file') {
                markdown += this.parseFileBlock(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'pdf') {
                markdown += this.parsePdfBlock(childBlock).concat(childBlockString);
            }
            if (childBlock.type === 'divider') {
                markdown += EOL_MD.concat('---', EOL_MD, childBlockString);
            }
            return markdown;
        }, '')
            .concat(EOL_MD);
    }
    parseParagraph(paragraphBlock) {
        let text;
        if (this.parserOptions.emptyParagraphToNonBreakingSpace &&
            paragraphBlock.paragraph.rich_text.length === 0) {
            text = '&nbsp;';
        }
        else {
            text = this.parseRichTexts(paragraphBlock.paragraph.rich_text);
        }
        return EOL_MD.concat(text, EOL_MD);
    }
    parseCodeBlock(codeBlock) {
        return `\`\`\`${codeBlock.code.language.toLowerCase() || ''}
${codeBlock.code.rich_text[0].text.content}
\`\`\``.concat(EOL_MD);
    }
    parseQuoteBlock(quoteBlock) {
        return EOL_MD.concat(`> ${this.parseRichTexts(quoteBlock.quote.rich_text)}`, EOL_MD);
    }
    parseCalloutBlock(calloutBlock) {
        const callout = `<div notion-callout>
  {{icon}}
  <span notion-callout-text>
    ${this.parseRichTexts(calloutBlock.callout.rich_text)}
  </span>
</div>`;
        function getCalloutIcon(icon) {
            switch (icon.type) {
                case 'emoji':
                    return `<span notion-callout-emoji>${icon.emoji}</span>`;
                case 'external':
                    return `<img notion-callout-external src='${icon.external.url}' alt='notion-callout-external-link'/>`;
                case 'file':
                    // TODO: add support for Callout File
                    return `notion-callout-file`;
            }
        }
        return EOL_MD.concat(callout.replace('{{icon}}', getCalloutIcon(calloutBlock.callout.icon)), EOL_MD);
    }
    parseHeading(headingBlock, headingLevel) {
        return EOL_MD.concat('#'.repeat(headingLevel), ' ', this.parseRichTexts(headingBlock[headingBlock.type].rich_text), EOL_MD);
    }
    parseBulletedListItems(bulletedListItemBlock) {
        return '* '.concat(this.parseRichTexts(bulletedListItemBlock.bulleted_list_item.rich_text), EOL_MD);
    }
    parseNumberedListItems(numberedListItemBlock) {
        return '1. '.concat(this.parseRichTexts(numberedListItemBlock.numbered_list_item.rich_text), EOL_MD);
    }
    parseTodoBlock(todoBlock) {
        return `- [${todoBlock.to_do.checked ? 'x' : ' '}] `.concat(this.parseRichTexts(todoBlock.to_do.rich_text), EOL_MD);
    }
    parseToggleBlock(toggleBlock) {
        return `<details><summary>${this.parseRichTexts(toggleBlock.toggle.rich_text)}</summary>{{childBlock}}</details>`;
    }
    parseImageBlock(imageBlock) {
        const { url, caption } = this.parseFile(imageBlock.image);
        if (this.parserOptions.imageAsFigure) {
            return `
<figure notion-figure>
  <img src='${url}' alt='${caption}'>
  <figcaption notion-figcaption>${caption}</figcaption>
</figure>
`.concat(EOL_MD);
        }
        return `![${caption}](${url})`.concat(EOL_MD);
    }
    parseAudioBlock(audioBlock) {
        const { url, caption } = this.parseFile(audioBlock.audio);
        return `![${caption}](${url})`;
    }
    parseVideoBlock(videoBlock) {
        const { url, caption } = this.parseFile(videoBlock.video);
        const [processed, iframeOrUrl] = (0, external_video_util_1.processExternalVideoUrl)(url);
        if (processed) {
            return EOL_MD.concat(iframeOrUrl, EOL_MD);
        }
        return `To be supported: ${url} with ${caption}`.concat(EOL_MD);
    }
    parseFileBlock(fileBlock) {
        const { url, caption } = this.parseFile(fileBlock.file);
        return `To be supported: ${url} with ${caption}`.concat(EOL_MD);
    }
    parsePdfBlock(pdfBlock) {
        const { url, caption } = this.parseFile(pdfBlock.pdf);
        return `
<figure>
  <object data='${url}' type='application/pdf'></object>
  <figcaption>${caption}</figcaption>
</figure>
`.concat(EOL_MD);
    }
    parseEmbedBlock(embedBlock) {
        const embedded = `<iframe src='${embedBlock.embed.url}'></iframe>`;
        if (embedBlock.embed.caption) {
            return `
<figure>
  ${embedded}
  <figcaption>${this.parseRichTexts(embedBlock.embed.caption)}</figcaption>
</figure>`.concat(EOL_MD);
        }
        return embedded.concat(EOL_MD);
    }
    parseRichTexts(richTexts) {
        return richTexts.reduce((parsedContent, richText) => {
            switch (richText.type) {
                case 'text':
                    parsedContent += this.parseText(richText);
                    break;
                case 'mention':
                    parsedContent += this.parseMention(richText);
                    break;
                case 'equation':
                    parsedContent += this.parseEquation(richText);
                    break;
            }
            return parsedContent;
        }, '');
    }
    parseText(richText) {
        const content = this.annotate(richText.annotations, richText.text.content);
        return richText.text.link
            ? this.annotateLink(richText.text, content)
            : content;
    }
    // TODO: support mention when we know what it actually means
    parseMention(mention) {
        switch (mention.mention.type) {
            case 'user':
                break;
            case 'page':
                break;
            case 'database':
                break;
            case 'date':
                break;
        }
        return this.annotate(mention.annotations, mention.plain_text);
    }
    parseEquation(equation) {
        return this.annotate(equation.annotations, `$${equation.equation.expression}$`);
    }
    parseFile(file) {
        const fileContent = {
            caption: '',
            url: '',
        };
        switch (file.type) {
            case 'external':
                fileContent.url = file.external.url;
                break;
            case 'file':
                fileContent.url = file.file.url;
                break;
        }
        fileContent.caption = file.caption
            ? this.parseRichTexts(file.caption)
            : fileContent.url;
        return fileContent;
    }
    annotate(annotations, originalContent) {
        return Object.entries(annotations).reduce((annotatedContent, [modifier, isOnOrColor]) => isOnOrColor
            ? this.annotateModifier(modifier, annotatedContent, isOnOrColor)
            : annotatedContent, originalContent);
    }
    annotateLink(text, annotatedContent) {
        return `[${annotatedContent}](${text.link.url ? text.link.url : text.link})`;
    }
    annotateModifier(modifier, originalContent, color) {
        switch (modifier) {
            case 'bold':
                return `**${originalContent}**`;
            case 'italic':
                return `_${originalContent}_`;
            case 'strikethrough':
                return `~~${originalContent}~~`;
            case 'underline':
                return `<u>${originalContent}</u>`;
            case 'code':
                return `\`${originalContent}\``;
            case 'color':
                if (color !== 'default') {
                    return `<span notion-color='${color}'>${originalContent}</span>`;
                }
                return originalContent;
        }
    }
}
exports.NotionBlocksMarkdownParser = NotionBlocksMarkdownParser;
//# sourceMappingURL=blocks-markdown-parser.js.map